import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Clock, ArrowRight, Send, Check, Loader2, AlertCircle } from "lucide-react";
import Reveal from "../components/Reveal";
import SectionDivider from "../components/SectionDivider";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const industries = [
  "Logistics & Freight",
  "Legal & Professional Services",
  "Marketing & Creative Agencies",
  "Real Estate",
  "Financial Services",
  "Healthcare",
  "E-commerce",
  "Other",
];

const initial = { first_name: "", last_name: "", company: "", email: "", industry: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.first_name || !form.last_name || !form.email || !form.message) {
      setError("Please fill in all required fields.");
      return;
    }
    setStatus("submitting");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm(initial);
    } catch (err) {
      setStatus("error");
      setError("Couldn't send your message. Please try again or email hello@zelaflo.com directly.");
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Zelaflo Labs — Book a Free Demo"
        description="Ready to automate? Send us a message or a sample PDF and we'll show you exactly what our document automation system extracts — completely free."
        path="/contact"
      />
      <section className="relative pt-40 pb-16 overflow-hidden" data-testid="contact-hero">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 z-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#7B2FBE] font-semibold mb-3">Contact</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter max-w-4xl text-balance" style={{ color: "var(--text)" }}>
              Let's Talk <span className="text-[#7B2FBE]">Automation.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-lg md:text-xl max-w-2xl" style={{ color: "var(--text-muted)" }}>
              No calls required. Just send us a message and we'll get back to you within one business day.
            </p>
          </Reveal>
        </div>
      </section>

      <SectionDivider />

      <section className="relative pb-24 md:pb-32" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-2 space-y-6">
            <div className="p-8 rounded-2xl border" style={{ backgroundColor: "var(--surface)", borderColor: "var(--border)" }}>
              <h3 className="font-heading text-2xl font-semibold mb-6" style={{ color: "var(--text)" }}>Reach Out</h3>
              <div className="space-y-5">
                <a href="mailto:hello@zelaflo.com" className="flex items-start gap-4 group" data-testid="contact-email-link">
                  <div className="w-12 h-12 rounded-xl bg-[#6B2FD9]/15 border border-[#6B2FD9]/30 flex items-center justify-center text-[#8A4FFF] group-hover:bg-[#6B2FD9] group-hover:text-white transition-all">
                    <Mail size={20} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--text-subtle)" }}>Email</div>
                    <div className="mt-1 text-base" style={{ color: "var(--text)" }}>hello@zelaflo.com</div>
                  </div>
                </a>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl border flex items-center justify-center" style={{ backgroundColor: "var(--card-tint)", borderColor: "var(--border)", color: "var(--text-muted)" }}>
                    <Clock size={20} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--text-subtle)" }}>Response time</div>
                    <div className="mt-1 text-base" style={{ color: "var(--text)" }}>Within 1 business day</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden p-8 rounded-2xl bg-[#6B2FD9]" data-testid="free-sample-card">
              <motion.div
                className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-white/15 blur-3xl"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-[#2DD4BF]/30 blur-3xl"
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              />
              <div className="relative">
                <span className="inline-block text-xs uppercase tracking-widest font-semibold text-white/80 bg-white/10 px-3 py-1 rounded-full">Free Offer</span>
                <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white mt-4 tracking-tight">Want to see it work before you commit?</h3>
                <p className="text-white/85 mt-3 leading-relaxed">
                  Send us any PDF from your business and we'll show you exactly what our system extracts — completely free.
                </p>
                <a
                  href="mailto:hello@zelaflo.com?subject=Sample%20PDF%20-%20Zelaflo%20Labs"
                  className="mt-6 inline-flex items-center gap-2 bg-white text-[#171412] px-6 py-3 rounded-full text-sm font-bold hover:bg-white/90 hover:scale-105 transition-all"
                  data-testid="contact-send-sample-btn"
                >
                  Send a Sample PDF <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            <form onSubmit={submit} className="p-8 md:p-10 rounded-2xl border" style={{ backgroundColor: "var(--surface)", borderColor: "var(--border)" }} data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="First Name" required>
                  <input value={form.first_name} onChange={update("first_name")} required type="text" className={inputCls} data-testid="input-first-name" />
                </Field>
                <Field label="Last Name" required>
                  <input value={form.last_name} onChange={update("last_name")} required type="text" className={inputCls} data-testid="input-last-name" />
                </Field>
                <Field label="Company Name">
                  <input value={form.company} onChange={update("company")} type="text" className={inputCls} data-testid="input-company" />
                </Field>
                <Field label="Email" required>
                  <input value={form.email} onChange={update("email")} required type="email" className={inputCls} data-testid="input-email" />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Industry">
                    <select value={form.industry} onChange={update("industry")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="input-industry">
                      <option value="">Select industry</option>
                      {industries.map((ind) => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field label="Message" required>
                    <textarea value={form.message} onChange={update("message")} required rows={5} className={inputCls} placeholder="Tell us a bit about your workflow and what you'd like to automate..." data-testid="input-message" />
                  </Field>
                </div>
              </div>

              {error && (
                <p className="mt-4 text-sm text-red-400 inline-flex items-center gap-2" data-testid="form-error">
                  <AlertCircle size={14} /> {error}
                </p>
              )}
              {status === "success" && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-3"
                  data-testid="form-success"
                >
                  <Check size={16} className="text-emerald-400 mt-0.5" />
                  <div>
                    <div className="text-emerald-400 font-semibold text-sm">Message sent.</div>
                    <div className="text-sm mt-0.5" style={{ color: "var(--text-muted)" }}>Your demo request has been delivered to hello@zelaflo.com. We'll be in touch within one business day.</div>
                  </div>
                </motion.div>
              )}

              <div className="mt-8">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="btn-primary-zl inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-semibold disabled:opacity-50"
                  data-testid="form-submit-btn"
                >
                  {status === "submitting" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {status === "submitting" ? "Sending..." : "Send Message"}
                </button>
                <p className="text-xs mt-3" style={{ color: "var(--text-subtle)" }}>We'll reply within one business day. No spam, ever.</p>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const inputCls =
  "w-full rounded-xl px-4 py-3 transition-all duration-200 focus:outline-none focus:border-[#6B2FD9]";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-semibold mb-2 inline-block" style={{ color: "var(--text-subtle)" }}>
      {label} {required && <span className="text-[#6B2FD9]">*</span>}
    </span>
    {React.cloneElement(children, {
      style: {
        backgroundColor: "var(--input-bg)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "var(--input-border)",
        color: "var(--text)",
      },
    })}
  </label>
);

export default Contact;
